import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AlertType = 'success' | 'warning' | 'error';

export interface AlertState {
    show: boolean;
    type: AlertType;
    title?: string;
    message: string;
    blocking: boolean;
    duration?: number;
    confirmText?: string;
    onClose?: () => void;
    scrollToId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AlertService {

    private timerId: any = null;

    private alertSubject = new BehaviorSubject<AlertState>({
        show: false,
        type: 'success',
        message: '',
        blocking: false,
        confirmText: 'Tutup'
    });

    alert$ = this.alertSubject.asObservable();

    private scrollTargetElement(elementId?: string): void {
        if (!elementId || typeof document === 'undefined') return;
        try {
            const el = document.getElementById(elementId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const focusable = el.querySelector('textarea, input, select, button') || el;
                if (focusable && (focusable instanceof HTMLTextAreaElement || focusable instanceof HTMLInputElement || focusable instanceof HTMLSelectElement)) {
                    setTimeout(() => {
                        try {
                            (focusable as HTMLElement).focus({ preventScroll: true });
                        } catch {
                            (focusable as HTMLElement).focus();
                        }
                    }, 120);
                }
            }
        } catch (e) {
            console.warn('Alert scrollTargetElement error:', e);
        }
    }

    private show(
        type: AlertType,
        message: string,
        options?: {
            title?: string;
            blocking?: boolean;
            duration?: number;
            confirmText?: string;
            onClose?: () => void;
            scrollToId?: string;
        }
    ) {
        // Clear previous timer if still running
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }

        const {
            title,
            blocking = false,
            duration = 2500,
            confirmText = 'Tutup',
            onClose,
            scrollToId
        } = options || {};

        // Blur any currently active element (e.g. submit button at bottom) to avoid restoring focus back to it on close
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        // Scroll immediately if scrollToId is provided
        if (scrollToId) {
            this.scrollTargetElement(scrollToId);
        }

        this.alertSubject.next({
            show: true,
            type,
            title,
            message,
            blocking,
            duration,
            confirmText,
            onClose,
            scrollToId
        });

        if (!blocking) {
            this.timerId = setTimeout(() => {
                this.dismiss();
            }, duration);
        }
    }

    success(message: string, options?: { title?: string; confirmText?: string; onClose?: () => void }) {
        this.show('success', message, {
            title: options?.title,
            blocking: true,
            confirmText: options?.confirmText ?? 'Tutup',
            onClose: options?.onClose
        });
    }

    successAuto(message: string, options?: { title?: string; duration?: number }) {
        this.show('success', message, {
            title: options?.title,
            blocking: false,
            duration: options?.duration ?? 2000,
        });
    }

    warning(message: string, options?: { title?: string; confirmText?: string; onClose?: () => void; scrollToId?: string }) {
        this.show('warning', message, {
            title: options?.title ?? 'Peringatan',
            blocking: true,
            confirmText: options?.confirmText ?? 'Tutup',
            onClose: options?.onClose,
            scrollToId: options?.scrollToId
        });
    }

    warningAuto(message: string, options?: { title?: string; duration?: number; confirmText?: string; onClose?: () => void; scrollToId?: string }) {
        // Warning selalu blocking (tidak auto-close) agar supir/user bisa membaca pesan
        this.show('warning', message, {
            title: options?.title ?? 'Peringatan',
            blocking: true,
            confirmText: options?.confirmText ?? 'Tutup',
            onClose: options?.onClose,
            scrollToId: options?.scrollToId
        });
    }

    error(message: string, options?: { title?: string; confirmText?: string; onClose?: () => void; scrollToId?: string }) {
        this.show('error', message, {
            title: options?.title ?? 'Terjadi Kesalahan',
            blocking: true,
            confirmText: options?.confirmText ?? 'Tutup',
            onClose: options?.onClose,
            scrollToId: options?.scrollToId
        });
    }

    errorAuto(message: string, options?: { title?: string; duration?: number; confirmText?: string; onClose?: () => void; scrollToId?: string }) {
        // Error selalu blocking (tidak auto-close) agar pesan kendala dapat dibaca
        this.show('error', message, {
            title: options?.title ?? 'Terjadi Kesalahan',
            blocking: true,
            confirmText: options?.confirmText ?? 'Tutup',
            onClose: options?.onClose,
            scrollToId: options?.scrollToId
        });
    }

    dismiss() {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }

        const current = this.alertSubject.value;
        if (!current.show) return;

        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        this.alertSubject.next({
            ...current,
            show: false
        });

        // Run re-scroll and onClose AFTER the modal backdrop is fully closed and body scroll is restored
        setTimeout(() => {
            if (current.scrollToId) {
                this.scrollTargetElement(current.scrollToId);
            }
            if (current.onClose) {
                try {
                    current.onClose();
                } catch (e) {
                    console.error('Error in alert onClose:', e);
                }
            }
        }, 80);
    }
}
